import React, { useState } from 'react';
import {
	Alert,
	Form,
	Modal,
	Stack,
} from 'react-bootstrap';
import { fakeRootType } from '../FakeRootType';
import { CreateQuoteInput, Territory, QuoteType, QuoteProductCategory } from '../powershadesApiTypes';
import apiCalls from '../PowerShadesAPIFunctions';
import SpinningWheel from '../Parts/SpinningWheel';
import Button from '../Parts/Button';
import { useIsPsAdmin } from '../hooks';

type Props = {
	territories: Territory[];
	quoteTypes: QuoteType[];
	onClose: () => void;
	ROOT: fakeRootType;
};

const PRODUCT_CATEGORY_OPTIONS: { value: QuoteProductCategory; label: string }[] = [
	{ value: 'roller_shades', label: 'Roller Shades' },
	{ value: 'awnings', label: 'Awnings' },
];

const SelectTerritoryQuoteTypeModal: React.FC<Props> = ({
	territories,
	quoteTypes,
	onClose,
	ROOT,
}) => {
	const isPsAdmin = useIsPsAdmin();
	const defaultTerritoryId = territories.length === 1 ? territories[0]?.id : undefined;
	const CediaQuoteType = quoteTypes?.find((qt) => qt.name === 'CEDIA');
	const defaultQuoteTypeId = CediaQuoteType?.id
		? CediaQuoteType.id
		: (quoteTypes?.length === 1 ? quoteTypes[0]?.id : undefined);

	const [territoryId, setTerritoryId] = useState<number | undefined>(defaultTerritoryId);
	const [quoteTypeId, setQuoteTypeId] = useState<number | undefined>(defaultQuoteTypeId);
	const [productCategory, setProductCategory] = useState<QuoteProductCategory | undefined>(undefined);
	const [title, setTitle] = useState("");
	const [referenceNumber, setReferenceNumber] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
		event?.preventDefault();

		if (!territoryId || !quoteTypeId) {
			setError("Please select both a territory and a quote type.");
			return;
		}

		if (!productCategory) {
			setError("Please select whether this quote is for roller shades or awnings.");
			return;
		}

		setLoading(true);
		setError(null);

		const input: CreateQuoteInput = {
			TerritoryId: territoryId,
			QuoteTypeId: quoteTypeId,
			Title: title,
			ReferenceNumber: referenceNumber,
			ProductCategory: productCategory,
		};

		apiCalls.createQuote(input).then((resp: any) => {
			const { success, quoteId } = resp.data;
			if (success) {
				ROOT.loadContent(`#Quote?quoteID=${quoteId}&initial=${true}`);
				onClose();
			} else {
				setError(resp.data?.message || "Failed to create quote. Please try again.");
				setLoading(false);
			}
		}).catch((err: any) => {
			console.error(err);
			setError(err?.response?.data?.message || "An error occurred. Please try again.");
			setLoading(false);
		});
	};

	return (
		<Modal
			show
			onHide={onClose}
			centered
			size="lg"
			contentClassName="bg-body text-body"
		>
			<Form onSubmit={handleSubmit}>
				<Modal.Header closeButton>
					<header>
						<Modal.Title as="h2">Create New Quote</Modal.Title>
					</header>
				</Modal.Header>

				<Modal.Body>
					<section>
						{error && <Alert variant="danger">{error}</Alert>}

						<fieldset>
							<Stack gap={3}>
								<Form.Group controlId="new-quote-title">
									<Form.Label>Title</Form.Label>
									<Form.Control
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										maxLength={255}
									/>
								</Form.Group>

								<Form.Group controlId="new-quote-reference">
									<Form.Label>Reference Number (optional)</Form.Label>
									<Form.Control
										value={referenceNumber}
										onChange={(e) => setReferenceNumber(e.target.value)}
										maxLength={255}
									/>
								</Form.Group>

								<Form.Group controlId="product-category-select">
									<Form.Label>Product Line</Form.Label>
									<Form.Select
										value={productCategory ?? ''}
										onChange={(e) => setProductCategory(e.target.value as QuoteProductCategory)}
										required
									>
										<option value="">Select product line...</option>
										{PRODUCT_CATEGORY_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</Form.Select>
								</Form.Group>

								<Form.Group controlId="territory-select" hidden={!isPsAdmin && territories.length <= 1}>
									<Form.Label>Territory</Form.Label>
									<Form.Select
										value={territoryId ?? ''}
										onChange={(e) => setTerritoryId(Number(e.target.value))}
										disabled={territories.length <= 1}
									>
										{!defaultTerritoryId && <option value="">Select a Territory...</option>}
										{territories.map((t) => (
											<option key={t.id} value={t.id}>{t.name}</option>
										))}
									</Form.Select>
								</Form.Group>

								<Form.Group controlId="quote-type-select" hidden={!isPsAdmin}>
									<Form.Label>Quote Type</Form.Label>
									<Form.Select
										value={quoteTypeId ?? ''}
										onChange={(e) => setQuoteTypeId(Number(e.target.value))}
										disabled={quoteTypes.length <= 1}
									>
										{!defaultQuoteTypeId && <option value="">Select a Quote Type...</option>}
										{quoteTypes?.map((qt) => (
											<option key={qt.id} value={qt.id}>{qt.name}</option>
										))}
									</Form.Select>
								</Form.Group>
							</Stack>
						</fieldset>
					</section>
				</Modal.Body>

				<Modal.Footer>
					<footer className="d-flex gap-2">
						<Button
							color="secondary"
							onClick={onClose}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							color="primary"
							onClick={() => handleSubmit()}
							disabled={loading || !territoryId || !quoteTypeId || !productCategory}
						>
							{loading ? <SpinningWheel /> : 'Create Quote'}
						</Button>
					</footer>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};

export default SelectTerritoryQuoteTypeModal;
